import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

class StockTransferApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/wms/stocktransfer');
  }

  protected transformNode(node: any) {
    // Transform stock transfer data if needed
    return node;
  }

  // Custom methods for stock transfer specific operations
  async autoPopulate(id: string) {
    return axiosInstance.post(`${this.baseURL}/${id}/auto-populate`);
  }

  // Handle custom action calls from data provider
  async 'auto-populate'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      return this.autoPopulate(meta.stockTransferId);
    }
    throw new Error('stockTransferId is required for auto-populate action');
  }

  async uploadAssortment(id: string, data: any) {
    return axiosInstance.post(`${this.baseURL}/${id}/upload-assortment`, data);
  }

  async uploadFilters(id: string, data: any) {
    return axiosInstance.post(`${this.baseURL}/${id}/upload-filters`, data);
  }

  async searchItems(id: string, searchQuery: any, page: number = 1, size: number = 50) {
    return axiosInstance.post(
      `${this.baseURL}/${id}/items/search?page=${page}&size=${size}`,
      searchQuery,
    );
  }

  async findByStatus(status: string) {
    return axiosInstance.get(`${this.baseURL}/by-status/${status}`);
  }

  async findByType(type: string) {
    return axiosInstance.get(`${this.baseURL}/by-type/${type}`);
  }

  async findByUseCase(useCase: string) {
    return axiosInstance.get(`${this.baseURL}/by-use-case/${useCase}`);
  }

  async findByRequestNumber(requestNumber: string) {
    return axiosInstance.get(`${this.baseURL}/by-request-number/${requestNumber}`);
  }

  async exportAssortment(id: string) {
    return axiosInstance.get(`${this.baseURL}/${id}/export-assortment`);
  }

  // Handle searchItems action for items lookup
  async 'search-items'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const page = payload?.page || 1;
      const size = payload?.size || 50;
      const searchQuery = payload || {};
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.stockTransferId}/items/search?page=${page}&size=${size}`,
        searchQuery,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for search-items action');
  }

  // Handle approve action
  async approve(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.post(`${this.baseURL}/${meta.stockTransferId}/approve`);
      return response.data;
    }
    throw new Error('stockTransferId is required for approve action');
  }

  // Handle export-assortment action
  async 'export-assortment'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.get(
        `${this.baseURL}/${meta.stockTransferId}/export-assortment`,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for export-assortment action');
  }

  // Handle upload-assortment action
  async 'upload-assortment'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.stockTransferId}/upload-assortment`,
        payload,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for upload-assortment action');
  }

  // Handle close action
  async close(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.post(`${this.baseURL}/${meta.stockTransferId}/close`);
      return response.data;
    }
    throw new Error('stockTransferId is required for close action');
  }

  // Handle reject action
  async reject(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.stockTransferId) {
      const rejectedBy = payload?.rejectedBy || 'Manual';
      const reason = payload?.reason || 'Manually rejected';
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.stockTransferId}/reject`,
        null,
        {
          params: { rejectedBy, reason },
        },
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for reject action');
  }

  // Handle export-unfulfillable-items action
  async 'export-unfulfillable-items'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.get(
        `${this.baseURL}/${meta.stockTransferId}/export-unfulfillable-items`,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for export-unfulfillable-items action');
  }

  // Handle remove-unfulfillable-items action
  async 'remove-unfulfillable-items'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.stockTransferId}/remove-unfulfillable-items`,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for remove-unfulfillable-items action');
  }

  // Handle delete-unfulfillable-items action (remove all unfulfillable items entirely)
  async 'delete-unfulfillable-items'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.stockTransferId}/delete-unfulfillable-items`,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for delete-unfulfillable-items action');
  }

  // Handle delete-item action (remove single item by ID)
  async 'delete-item'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId && meta?.itemId) {
      const response = await axiosInstance.delete(
        `${this.baseURL}/${meta.stockTransferId}/items/${meta.itemId}`,
      );
      return response.data;
    }
    throw new Error('stockTransferId and itemId are required for delete-item action');
  }

  // Handle delete-all-items action (reset - remove all items)
  async 'delete-all-items'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.stockTransferId) {
      const response = await axiosInstance.delete(
        `${this.baseURL}/${meta.stockTransferId}/items`,
      );
      return response.data;
    }
    throw new Error('stockTransferId is required for delete-all-items action');
  }
}

export default StockTransferApiClient;
